/**
 * @file PatientQRView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import "../QR.css"
import {  Alert, Avatar, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { QrExpiryTimerView } from "../../../utils/QrExpiryTimerView";
import { ThemeProvider } from "@emotion/react";
import { QRGeneratedTitle } from "../../patient/themes/AddPatientDialogTheme";
import { Img } from "../../../common/customviews/AppImg";
import { BaseComponent } from "../../base/BaseComponent";
import useWindowDimensions from "../../../utils/useWindowDimensions";
import html2canvas from 'html2canvas';
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { AppName } from "../../../utils/AppConstant";
import { AppUtility } from "../../../utils/AppUtility";

/**
 * A type of Component state
 */
type CompState = {
    isQRexpired: boolean, backDropLoading: boolean, errorMessage: string, errorToggle: boolean
};

/**
 * An interface definition for Component Props
 */
interface Props {
    qrContent: string;
    qrUserName: string;
    qrloading: boolean;
    onDialogDismissed: any
}

/**
 * A class definition for PatientListView
 */
export class PatientQRView extends BaseComponent<Props, CompState> {

    readonly time = 2 * 24 * 60 * 60;

    constructor(props: Props) {
        super(props);
        this.state = {
            isQRexpired: false, errorToggle: false, backDropLoading: false, errorMessage: "",
        };
    }

    /**
     * Hides loading
     * @param message string
     */
    private hideLoading = ()=> {
        this.setState({
            backDropLoading: false
        });
    }

    /**
     * Show Error
     * @param message string
     */
    private showError = (message: string)=> {
        this.setState({
            errorMessage : message, errorToggle : true,
            backDropLoading: false
        });
    }    

    /**
     * Shows loading
     */
    private showLoading = ()=>{
        this.setState({
            errorToggle: false,
            backDropLoading: true
        })
    }

    /**
     * Handles backdrop close
     */
    private handleBackDropClose = () => this.setState({ backDropLoading: false});

    /**
     * Dimisses dialog
     */
    private dismissDialog = () => {
        this.props.onDialogDismissed();
    }
    
    /**
     * Handles QR expiray
     */
    private handleQrExirey = () => {
        this.setState({ isQRexpired: true });
    }

    private convertImageAndShare = async ()=> {
        this.showLoading();
        const canvas = document.getElementById("qr-code-image")!!; // eslint-disable-line
        html2canvas(canvas).then(async (canvas)=> {
            canvas.toBlob(async (blob) => {            
            const data = {
                files: [
                  new File([blob!!], 'image.png', { // eslint-disable-line
                    type: "image/png",
                  }),
                ],
                title: `${AppName} QR Code`
              };
              try {
                await navigator.share(data);
                this.hideLoading();
             } catch (err) {
               this.logger.warning(JSON.stringify(err), PatientQRView.name);
               this.showError("Error while sharing the QR.");
             }
            })
        });
    }

    render() {
        return <>
            {this.props.qrloading && (<div className="QrcodeProgressBox" >
                <CircularProgress size={80} thickness={4}
                /> </div>)}
            {!this.props.qrloading && (
                <div>
                    <div className="QrcodeBox">
                        <Grid container spacing={0} className="QrcodeBoxGrid">
                            <Grid item xs={9}>
                            </Grid>
                            <Grid item xs={3} container justifyContent="flex-end">
                             <IconButton onClick={this.dismissDialog}> <Avatar src={"/../closeIcon.svg"} /></IconButton>
                            </Grid>
                        </Grid>
                        <div className="Qrcode" id="qr-code-image">
                                <QrCodeSvg qrContent={this.props.qrContent} id="qrCode"/>
                        </div>
                        {this.state.isQRexpired && (<div className="Expiry">
                            <Typography variant="h6" component="h1" style={{ fontSize: "2em", fontStyle: "bold", color: "red" }} >
                                Expired
                            </Typography></div>)}
                        <div>
                            <ThemeProvider theme={QRGeneratedTitle}>
                            <Typography variant="h6" align="center" display="block" className="SuccessText">
                                Your QR Code was generated <br />successfully
                            </Typography>
                            </ThemeProvider>
                            <Typography style={{ fontSize: "0.8em", width: "100%", lineHeight: "2vh" , marginTop: "1vh"}} align="center">
                                Your can access it anytime from the patient <br />records list
                            </Typography>
                            <QrExpiryTimerView expiredTime={this.time} expiryTrigger={this.handleQrExirey} />

                            <Grid container spacing={0} style={{ marginTop: "1.5vh",marginBottom:"2vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <Img alt="complex" src="/../share.svg"  style={{  cursor: "pointer"}}  onClick={()=>{
                                    this.convertImageAndShare()
                                }}/>
                            </Grid>
                        </div>
                    </div>
                    <BackDropLoadingView handleBackDropClose={this.handleBackDropClose} isLoading={this.state.backDropLoading} />
                {
                    this.state.errorToggle && <Alert severity="error">
                        {this.state.errorMessage}
                    </Alert>
                }
                </div>
            )}
        </>
    }
}

/**
 * Functional component for QR
 * @param props any
 * @returns 
 */
const QrCodeSvg = (props:any)=> {
    const { width} = useWindowDimensions();
    let size = 0;
    if(AppUtility.isMobileDeviceScreen())
    {
        size = width/3;
    }
    else{
        size = width/7;
    }
    return (<QRCodeSVG value={props.qrContent} height={size} width={size}/>)
}

